import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import useCanManageApiKeys from "shared/ApiKey/access/useCanManageApiKeys";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import Box from "ds/components/Box";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Tab from "ds/components/Tab";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useTitle from "hooks/useTitle";
import { showDeleteConfirmation } from "components/ApiKeys/DeleteConfirmation";
import { showApiKeysDetailsDrawer } from "views/Account/Settings/ApiKey/DetailsDrawer";
import { showApiKeysEditDrawer } from "views/Account/Settings/ApiKey/EditDrawer";
import { encodeApiKeyId } from "shared/ApiKey/urlHelpers";

import { API_KEYS_FILTERS_ORDER_SETTINGS_KEY } from "../ApiKeysV2/List/constants";
import { ApiKeyView } from "./types";
import { GET_API_KEYS } from "../ApiKeysV2/gql";

type ApiKeyPageLayoutProps = {
  children: ReactNode;
  apiKey: ApiKeyView;
};

const ApiKeyPageLayout = ({ children, apiKey }: ApiKeyPageLayoutProps) => {
  const navigate = useNavigate();
  const canManageApiKeys = useCanManageApiKeys();

  const apiKeyUrl = `/settings/api-key/${encodeApiKeyId(apiKey.id)}`;

  const backURL = getBreadcrumbsBackUrl("/settings/api-keys", API_KEYS_FILTERS_ORDER_SETTINGS_KEY);

  // Set breadcrumbs and title at the layout level
  useTitle(`Organization Settings · API key · ${apiKey.name}`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "API keys",
        link: backURL,
      },
      {
        title: apiKey.name,
      },
    ],
    [apiKey.name]
  );

  const handleDeleteApiKey = () => {
    showDeleteConfirmation({
      name: apiKey.name,
      id: apiKey.id,
      onDelete: () => navigate(backURL),
      refetchQueriesOnDelete: [GET_API_KEYS],
    });
  };

  const openApiKeyDetails = () => {
    showApiKeysDetailsDrawer({
      id: apiKey.id,
      apiKey,
    });
  };

  const openEditDetailsDrawer = () => {
    showApiKeysEditDrawer({
      apiKey,
    });
  };

  return (
    <>
      <ViewHeader firstLevel>
        <Box fullWidth justify="between" align="center">
          <Box gap="medium" align="center">
            <ViewHeaderTitle tag="h2">{apiKey.name}</ViewHeaderTitle>
          </Box>

          <Box gap="medium" align="center">
            <Button variant="secondary" onPress={openApiKeyDetails}>
              Details
            </Button>

            <DropdownMenuEllipsis tooltip="API key actions" dotsSize="medium">
              <CopyFieldDropdownMenuItem title="Copy ID" value={apiKey.id} />
              {canManageApiKeys && (
                <DropdownMenuItem onAction={openEditDetailsDrawer}>Edit details</DropdownMenuItem>
              )}
              {canManageApiKeys && (
                <DropdownMenuItem onAction={handleDeleteApiKey} danger>
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuEllipsis>
          </Box>
        </Box>
        <ViewHeaderNavigation role="tablist">
          <Tab end to={apiKeyUrl} label="Roles" />
          <Tab to={`${apiKeyUrl}/group-mapping`} label="Group mapping" />
        </ViewHeaderNavigation>
      </ViewHeader>

      {children}
    </>
  );
};

export default ApiKeyPageLayout;
